import { useState, useRef, useEffect } from "react";
import { ExternalLink } from "lucide-react";
import type { Citation } from "../types";

interface CitationInlineProps {
  citations: Citation[];
}

function CitationPill({
  citation,
}: {
  citation: Citation;
}) {
  const [showCard, setShowCard] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!showCard) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowCard(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCard]);

  return (
    <span ref={containerRef} className="relative inline-block">
      <button
        type="button"
        className="mx-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded bg-primary/10 px-1 text-[10px] font-semibold text-primary hover:bg-primary/20 transition-colors"
        onMouseEnter={() => setShowCard(true)}
        onMouseLeave={() => setShowCard(false)}
        onClick={() => setShowCard((s) => !s)}
        aria-label={`Citation ${citation.number}: ${citation.title}`}
      >
        {citation.number}
      </button>

      {showCard && (
        <div className="absolute bottom-full left-0 z-50 mb-1 w-72 rounded-lg border border-border bg-popover p-3 shadow-lg">
          <a
            href={citation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-2"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-primary group-hover:underline line-clamp-2">
                {citation.title}
              </p>
              {citation.description && (
                <p className="mt-1 text-xs text-muted-foreground line-clamp-3">
                  {citation.description}
                </p>
              )}
              {citation.quote && (
                <blockquote className="mt-1.5 border-l-2 border-muted pl-2 text-xs italic text-muted-foreground line-clamp-3">
                  &ldquo;{citation.quote}&rdquo;
                </blockquote>
              )}
              <p className="mt-1 text-[10px] text-muted-foreground/70 truncate">
                {citation.url}
              </p>
            </div>
            <ExternalLink className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
          </a>
        </div>
      )}
    </span>
  );
}

export function CitationInline({ citations }: CitationInlineProps) {
  return (
    <span className="inline">
      {citations.map((c) => (
        <CitationPill key={c.number} citation={c} />
      ))}
    </span>
  );
}
